function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const parts = value.split('.');
  const withGroups = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const formatted = `${withGroups},${parts[1]}`;
  return format.replace(/\{\{\s*amount\s*\}\}/, formatted);
}

class OmtProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-omt-gallery-main]');
    this.image = this.querySelector('[data-omt-gallery-image]');
    this.querySelectorAll('[data-omt-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.selectThumb(thumb));
    });
    this.main?.addEventListener('click', (event) => this.toggleZoom(event));
    this.main?.addEventListener('mousemove', (event) => this.trackZoom(event));
    this.main?.addEventListener('mouseleave', () => this.main.classList.remove('is-zoomed'));
  }

  selectThumb(thumb) {
    this.querySelectorAll('[data-omt-thumb]').forEach((t) => {
      t.classList.toggle('is-active', t === thumb);
      t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
    });

    if (thumb.dataset.mediaType === 'image') {
      this.main.innerHTML = '';
      const img = document.createElement('img');
      img.src = thumb.dataset.fullSrc;
      img.width = thumb.dataset.fullWidth;
      img.height = thumb.dataset.fullHeight;
      img.alt = thumb.dataset.fullAlt || '';
      img.setAttribute('data-omt-gallery-image', '');
      this.main.appendChild(img);
      this.image = img;
      this.main.classList.remove('is-zoomed');
      return;
    }

    const template = this.querySelector(`[data-omt-media-template][data-media-id="${thumb.dataset.mediaId}"]`);
    if (template) {
      this.main.innerHTML = '';
      this.main.appendChild(template.content.cloneNode(true));
      this.main.classList.remove('is-zoomed');
    }
  }

  toggleZoom(event) {
    if (!this.main.contains(this.image) || !this.image.isConnected) return;
    this.main.classList.toggle('is-zoomed');
    this.trackZoom(event);
  }

  trackZoom(event) {
    if (!this.main.classList.contains('is-zoomed')) return;
    const rect = this.main.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.main.style.setProperty('--zoom-x', `${x}%`);
    this.main.style.setProperty('--zoom-y', `${y}%`);
  }
}
customElements.define('omt-product-gallery', OmtProductGallery);

class OmtVariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-omt-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.moneyFormat = window.Shopify?.money_format || '{{amount}} €';
    this.selected = {};
    this.querySelectorAll('[data-omt-option]').forEach((row) => {
      const position = row.dataset.optionPosition;
      const checked = row.querySelector('[aria-checked="true"]');
      if (checked) this.selected[position] = checked.dataset.omtOptionValue;
      const buttons = Array.from(row.querySelectorAll('[data-omt-option-value]'));
      buttons.forEach((button, i) => {
        button.addEventListener('click', () => this.chooseValue(row, position, button));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          const next = buttons[(i + (forward ? 1 : buttons.length - 1)) % buttons.length];
          next.focus();
          this.chooseValue(row, position, next);
        });
      });
    });
    this.qtyInput = this.querySelector('.pdp-buy-row input[type="number"]');
    this.querySelector('[data-omt-qty-decrease]')?.addEventListener('click', () => {
      this.qtyInput.value = Math.max(1, parseInt(this.qtyInput.value, 10) - 1);
    });
    this.querySelector('[data-omt-qty-increase]')?.addEventListener('click', () => {
      this.qtyInput.value = parseInt(this.qtyInput.value, 10) + 1;
    });
  }

  chooseValue(row, position, button) {
    row.querySelectorAll('[data-omt-option-value]').forEach((b) => {
      const isSelected = b === button;
      b.setAttribute('aria-checked', String(isSelected));
      b.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
    this.selected[position] = button.dataset.omtOptionValue;
    const match = this.variants.find((variant) =>
      [variant.option1, variant.option2, variant.option3].every((value, index) => {
        const wanted = this.selected[index + 1];
        return wanted === undefined || wanted === null || value === wanted;
      })
    );
    if (match) this.applyVariant(match);
  }

  applyVariant(variant) {
    const input = this.querySelector('[data-omt-variant-id]');
    if (input) input.value = variant.id;

    const priceWrap = this.querySelector('[data-omt-price] .price b');
    if (priceWrap) priceWrap.textContent = formatMoney(variant.price, this.moneyFormat);
    const compareEl = this.querySelector('[data-omt-price] .price s');
    if (variant.compare_at_price > variant.price) {
      const formatted = formatMoney(variant.compare_at_price, this.moneyFormat);
      if (compareEl) compareEl.textContent = formatted;
      else this.querySelector('[data-omt-price] .price')?.insertAdjacentHTML('beforeend', `<s class="dim">${formatted}</s>`);
    } else if (compareEl) {
      compareEl.remove();
    }

    const addButton = this.querySelector('[data-omt-add-button]');
    const addLabel = this.querySelector('[data-omt-add-label]');
    if (addButton) addButton.toggleAttribute('disabled', !variant.available);
    if (addLabel) addLabel.textContent = variant.available ? addLabel.dataset.addText : addLabel.dataset.soldOutText;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (variant.featured_image) {
      const filename = variant.featured_image.src.split('/').pop().split('?')[0];
      const gallery = this.closest('.pdp-grid')?.querySelector('omt-product-gallery');
      const thumb = gallery?.querySelector(`[data-omt-thumb][data-image-filename="${filename}"]`);
      if (thumb) gallery.selectThumb(thumb);
    }
  }
}
customElements.define('omt-variant-picker', OmtVariantPicker);
